import { useState, useEffect, MutableRefObject } from "react";
import { useQuery, useMutation } from "react-query";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import { geojsonToArcGIS } from "@terraformer/arcgis";
import {
  getArcGISGeometryType,
  getSymbolForGeometryType,
  convertToGeoJSON,
  getHoverSymbolForGeometryType,
} from "../../utils/arcgisHelper";
import { partition, Dictionary, keyBy } from "lodash";
import useNetworkHeaders from "../../../../hooks/useNetworkHeaders";
import { getAdhocFeatures, bulkUpdateAdhocFeatures, BulkAdhocUpdateParams } from "../../queries/maps";

export interface AdhocFeatureTracking {
  [graphicId: string]: {
    featureId?: number;
    isNew?: boolean;
    isModified?: boolean;
    isDeleted?: boolean;
    originalGeometry?: any;
  };
}

export const ADHOC_LAYER_ID = "adhoc";

interface UseAdhocFeaturesOptions {
  editLayerRef: MutableRefObject<GraphicsLayer | null>;
  featureTracking: AdhocFeatureTracking;
  selectedFeatures: { [featureId: string]: boolean };
  initialSelectionLoaded: boolean;
  setFeatureTracking: React.Dispatch<React.SetStateAction<AdhocFeatureTracking>>;
  markFeatureAsChangedDebounced: ((graphicId: string) => void) | null;
}

export function useAdhocFeatures({
  editLayerRef,
  featureTracking,
  selectedFeatures,
  initialSelectionLoaded,
  setFeatureTracking,
  markFeatureAsChangedDebounced,
}: UseAdhocFeaturesOptions) {
  const [isSaving, setIsSaving] = useState(false);
  const networkHeaders = useNetworkHeaders();

  // Query for fetching all adhoc features
  const { data: adhocFeatures, refetch } = useQuery(["adhoc-features"], getAdhocFeatures(networkHeaders), {
    refetchOnWindowFocus: false,
  });

  const [bulkUpdate] = useMutation(bulkUpdateAdhocFeatures(networkHeaders), {
    onSuccess: () => refetch(),
  });

  useEffect(() => {
    if (adhocFeatures && editLayerRef.current) {
      // Clear any existing adhoc features
      const existingAdhocGraphics = editLayerRef.current.graphics
        .toArray()
        .filter((g) => g.attributes?.graphicId?.startsWith("adhoc-"));

      if (existingAdhocGraphics.length > 0) {
        editLayerRef.current.graphics.removeMany(existingAdhocGraphics);
      }

      // Reset tracking for adhoc features
      setFeatureTracking((prevTracking) => {
        const newTracking = { ...prevTracking };
        Object.keys(newTracking).forEach((key) => {
          if (key.startsWith("adhoc-")) delete newTracking[key];
        });
        return newTracking;
      });

      const graphics = adhocFeatures.map((feature) => {
        const graphicId = `adhoc-${feature.id}`;
        const symbol = selectedFeatures[graphicId]
          ? getHoverSymbolForGeometryType(feature.geometry.type)
          : getSymbolForGeometryType(feature.geometry.type);
        const graphic = new Graphic({
          geometry: {
            ...geojsonToArcGIS(feature.geometry as GeoJSON.GeoJSON),
            type: getArcGISGeometryType(feature.geometry.type),
          },
          attributes: {
            graphicId,
            featureId: feature.id,
            name: feature.name || `Ad hoc Feature ${feature.id}`,
          },
          symbol,
        });

        setFeatureTracking((prev) => ({
          ...prev,
          [graphicId]: {
            featureId: feature.id,
            originalGeometry: feature.geometry,
          },
        }));

        markFeatureAsChangedDebounced?.(graphicId);

        return graphic;
      });

      editLayerRef.current.graphics.addMany(graphics);
    }
  }, [adhocFeatures, !!editLayerRef.current, initialSelectionLoaded]);

  // Save adhoc features (create, update, delete)
  const saveAdhocFeatures = async () => {
    if (!editLayerRef.current) return {};

    try {
      setIsSaving(true);

      const graphics = editLayerRef.current.graphics
        .toArray()
        .filter((g) => g.attributes?.graphicId?.startsWith("adhoc-"));

      const graphicIdMap = keyBy(graphics, (g) => g.attributes?.graphicId) as Dictionary<Graphic>;

      type IndexedCreates = Extract<BulkAdhocUpdateParams[number], { mode: "Create" }> & {
        index: number; // We need to sort this before sending it so created features are still in order
      };

      const bulkUpdatePayload: (Exclude<BulkAdhocUpdateParams[number], { mode: "Create" }> | IndexedCreates)[] = [];

      await Promise.all(
        Object.entries(featureTracking)
          .filter(([graphicId]) => graphicId.startsWith("adhoc-"))
          .map(async ([graphicId, tracking]) => {
            const graphic = graphicIdMap[graphicId];
            if (!graphic && !tracking.isDeleted) return;

            if (tracking.isDeleted && tracking.featureId) {
              bulkUpdatePayload.push({
                mode: "Delete",
                featureId: tracking.featureId,
              });
              return;
            }

            if (!graphic?.geometry) return;
            const geoJSONGeometry = await convertToGeoJSON(graphic.geometry);
            const name = graphic.attributes.name || `Ad hoc Feature`;

            if (tracking.isNew) {
              bulkUpdatePayload.push({
                mode: "Create",
                geometry: geoJSONGeometry,
                name: name,
                tempId: graphic.attributes.graphicId,
                index: graphic.attributes.index || 0,
              });
              return;
            }

            if (tracking.isModified && tracking.featureId) {
              bulkUpdatePayload.push({
                mode: "Edit",
                featureId: tracking.featureId,
                geometry: geoJSONGeometry,
                name: name,
              });
              return;
            }
          }),
      );

      const [createObjects, editDeleteObjects] = partition(bulkUpdatePayload, (payload) => payload.mode == "Create");

      const sortedCreates = (createObjects as IndexedCreates[]).sort((a, b) => a.index - b.index);
      const data = [...sortedCreates, ...editDeleteObjects];

      let createMap: Record<string, number> = {};
      if (data.length > 0) {
        const response = await bulkUpdate(data);
        if (response?.createMap) createMap = response.createMap;
      }

      await refetch();
      setIsSaving(false);

      return createMap;
    } catch (error) {
      console.error("Error saving adhoc features:", error);
      setIsSaving(false);
      throw error;
    }
  };

  return {
    adhocFeatures,
    isSaving,
    saveAdhocFeatures,
    refetchAdhocFeatures: refetch,
  };
}
